import * as React from 'react';
import {useDispatch} from 'react-redux';

import type {FilterObject} from 'nutshell-core/types';

import {ReportChart} from '../../../reports/report-chart';
import {ReportDataSummary} from '../../../reports/report-data-summary';
import {WIDGET_OPENS} from '../../../reports/report-types';
import type {ChartReportParams, ReportURLParams} from '../../../reports/report-types';

import * as actions from './widget-opens-actions';
import {WidgetOpensReportProvider} from './widget-opens-report-provider';

type Props = {
    activeFilters: FilterObject[];
    onChartClick?: (filters: FilterObject) => void | null | undefined;
    onParamChange: (arg1: any, arg2: boolean) => void;
    reportParams: ChartReportParams;
    // This chart does not use the list view so this can be "*"
    urlParams: ReportURLParams<any>;
    currentGapIndex: number;
};

export function WidgetOpensChart(props: Props) {
    const dispatch = useDispatch();

    const fetchReportData = React.useCallback(
        (reportParams: ChartReportParams) => {
            dispatch(
                actions.requestReportData({
                    reportParams: {
                        ...reportParams,
                        period:
                            (props.urlParams.filter && props.urlParams.filter.createdTime) ||
                            '-d30',
                    },
                })
            );
        },
        [dispatch, props.urlParams.filter]
    );

    const activeFiltersString = JSON.stringify(props.urlParams.filter);
    const reportParamsString = JSON.stringify(props.reportParams);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedReportParams = React.useMemo(() => props.reportParams, [reportParamsString]);

    React.useEffect(() => {
        fetchReportData(memoizedReportParams);
    }, [fetchReportData, memoizedReportParams, activeFiltersString]);

    const reportDataSummary = (
        <ReportDataSummary
            currentGapIndex={props.currentGapIndex}
            filterParams={props.urlParams.filter}
            reportParams={memoizedReportParams}
            reportKey={WIDGET_OPENS}
        />
    );

    return (
        <WidgetOpensReportProvider>
            <ReportChart
                {...props}
                reportParams={{...memoizedReportParams, facet: 'count'}}
                reportKey={WIDGET_OPENS}
                hasBorder={false}
                isFullWidth={true}
                reportDataSummary={reportDataSummary}
            />
        </WidgetOpensReportProvider>
    );
}
